import React from "react";
import { Link, Navigate } from "react-router-dom";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import { getStoredToken } from "../../api/client";
import { getDevUsers, updateDevUserPermissions } from "../../api/devUsers";

const PRIORITY_COLUMNS = [
  "id",
  "email",
  "plan",
  "plan_status",
  "monthly_photo_limit",
  "usage_photo_count",
  "usage_month_start",
  "stripe_customer_id",
  "stripe_subscription_id",
  "current_period_start",
  "current_period_end",
  "password_algo",
  "created_at",
  "updated_at",
];

const PLAN_OPTIONS = ["free", "starter", "pro", "enterprise"];
const STATUS_OPTIONS = ["active", "active_annual", "canceled", "incomplete", "trialing"];

function formatCell(value) {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

export default function DevUsersSettings() {
  const token = getStoredToken();
  const [data, setData] = React.useState({ count: 0, users: [] });
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState("");
  const [error, setError] = React.useState("");
  const [edits, setEdits] = React.useState({});

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const out = await getDevUsers();
      const users = Array.isArray(out?.users) ? out.users : [];
      setData({ count: Number(out?.count || users.length), users });
      setEdits((prev) => {
        const next = { ...prev };
        users.forEach((u) => {
          const id = String(u?.id || "");
          if (!id || next[id]) return;
          next[id] = {
            plan: String(u?.plan || "free"),
            plan_status: String(u?.plan_status || "active"),
            monthly_photo_limit: String(u?.monthly_photo_limit ?? "50"),
          };
        });
        return next;
      });
    } catch (e) {
      setData({ count: 0, users: [] });
      setError(e?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const columns = React.useMemo(() => {
    const rows = Array.isArray(data?.users) ? data.users : [];
    const discovered = new Set();
    rows.forEach((row) => {
      Object.keys(row || {}).forEach((k) => discovered.add(k));
    });
    const ordered = [];
    PRIORITY_COLUMNS.forEach((k) => {
      if (discovered.has(k)) ordered.push(k);
    });
    Array.from(discovered)
      .filter((k) => !ordered.includes(k))
      .sort()
      .forEach((k) => ordered.push(k));
    return ordered;
  }, [data]);

  const savePermissions = async (userId) => {
    const id = String(userId || "");
    if (!id) return;
    const row = edits[id] || {};
    setSavingId(id);
    setError("");
    try {
      await updateDevUserPermissions(id, {
        plan: String(row.plan || "free"),
        plan_status: String(row.plan_status || "active"),
        monthly_photo_limit: Number(row.monthly_photo_limit || 0),
      });
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to update permissions");
    } finally {
      setSavingId("");
    }
  };

  if (!token) {
    return <Navigate to="/pricing" replace />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Dev Users</h1>
          <p className="mt-1 text-sm text-slate-500">
            View users from your dev account. Access is restricted to the configured dev login only.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={refresh} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </Button>
            <Link to="/app/growth">
              <Button>Growth Console</Button>
            </Link>
          </div>

          {error ? (
            <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">
              {error.includes("403") ? `${error}. Sign in as your configured dev account.` : error}
            </p>
          ) : null}

          {!error ? (
            <p className="text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{Number(data?.count || 0)}</span> users.
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-slate-900">Users Table</h2>
        </CardHeader>
        <CardBody>
          {loading ? <p className="text-sm text-slate-500">Loading users...</p> : null}
          {!loading && !error && (!data?.users || data.users.length === 0) ? (
            <p className="text-sm text-slate-500">No users found.</p>
          ) : null}
          {!loading && !error && data?.users?.length ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {columns.map((col) => (
                      <th key={col} className="whitespace-nowrap px-3 py-2 text-left text-slate-600">
                        {col}
                      </th>
                    ))}
                    <th className="whitespace-nowrap px-3 py-2 text-left text-slate-600">Permissions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((row, idx) => {
                    const id = String(row?.id || "");
                    const edit = edits[id] || {
                      plan: String(row?.plan || "free"),
                      plan_status: String(row?.plan_status || "active"),
                      monthly_photo_limit: String(row?.monthly_photo_limit ?? "50"),
                    };
                    return (
                      <tr key={id || String(idx)} className="border-t border-slate-100 align-top">
                        {columns.map((col) => (
                          <td key={`${idx}:${col}`} className="whitespace-nowrap px-3 py-2 text-slate-700">
                            {formatCell(row?.[col])}
                          </td>
                        ))}
                        <td className="px-3 py-2">
                          <div className="flex min-w-[340px] items-center gap-2">
                            <select
                              className="h-9 rounded border border-slate-300 bg-white px-2"
                              value={edit.plan}
                              onChange={(e) => setEdits((prev) => ({ ...prev, [id]: { ...edit, plan: e.target.value } }))}
                            >
                              {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                            <select
                              className="h-9 rounded border border-slate-300 bg-white px-2"
                              value={edit.plan_status}
                              onChange={(e) => setEdits((prev) => ({ ...prev, [id]: { ...edit, plan_status: e.target.value } }))}
                            >
                              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <Input
                              className="h-9 w-28"
                              value={edit.monthly_photo_limit}
                              onChange={(e) => setEdits((prev) => ({ ...prev, [id]: { ...edit, monthly_photo_limit: e.target.value } }))}
                              placeholder="Limit"
                            />
                            <Button size="sm" onClick={() => savePermissions(id)} disabled={savingId === id}>
                              {savingId === id ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
